import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import apiClient from '../services/apiClient';
import ScenePlayer from './ScenePlayer';
import LearningCoach from './LearningCoach';
import './AdminSceneBrowser.css';

export default function AdminSceneBrowser() {
  const [authorized, setAuthorized] = useState(() => Boolean(apiClient.getAdminToken()));
  const [catalog, setCatalog] = useState({ scenes: [], triggers: [], chapters: [] });
  const [trigger, setTrigger] = useState('');
  const [chapter, setChapter] = useState('');
  const [index, setIndex] = useState(0);
  const [reload, setReload] = useState(0);
  const [replay, setReplay] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sceneLoading, setSceneLoading] = useState(false);
  const [error, setError] = useState('');
  const [sceneError, setSceneError] = useState('');
  const [loaded, setLoaded] = useState(null);
  const [finished, setFinished] = useState(false);
  const handleError = useCallback((err, setter) => {
    if (err.status === 401) { apiClient.clearAdminToken(); setAuthorized(false); }
    else setter(err.message || 'Scene을 불러오지 못했습니다.');
  }, []);

  useEffect(() => {
    if (!authorized) return;
    let cancelled = false;
    setLoading(true); setError(''); setLoaded(null);
    apiClient.getAdminSceneBrowser().then(result => {
      if (cancelled) return;
      setCatalog(result); setIndex(0); setTrigger(''); setChapter('');
    }).catch(err => { if (!cancelled) handleError(err, setError); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [authorized, reload, handleError]);

  const filtered = useMemo(() => catalog.scenes.filter(item =>
    (!trigger || item.trigger === trigger) && (!chapter || (chapter === '__none' ? !item.chapterId : item.chapterId === chapter))),
  [catalog.scenes, trigger, chapter]);
  const selectedId = filtered[index]?.id;
  useEffect(() => {
    if (!authorized || !selectedId || loading || error) return;
    let cancelled = false;
    setSceneLoading(true); setSceneError(''); setLoaded(null); setFinished(false);
    apiClient.getAdminScenePreview(selectedId).then(result => {
      if (!cancelled) setLoaded({ id: selectedId, scene: result.scene });
    }).catch(err => { if (!cancelled) handleError(err, setSceneError); })
      .finally(() => { if (!cancelled) setSceneLoading(false); });
    return () => { cancelled = true; };
  }, [authorized, selectedId, loading, error, replay, handleError]);

  if (!authorized) return <Navigate to="/admin" replace />;
  const scene = !loading && !error && loaded?.id === selectedId ? loaded.scene : null;
  const filter = setter => event => { setter(event.target.value); setIndex(0); };
  return <div className="admin-scene-browser">
    <header className="admin-scene-browser-toolbar">
      <div className="admin-scene-browser-heading">
        <div><Link to="/admin">← 관리자</Link><h1>Scene 둘러보기</h1></div>
        <button type="button" disabled={loading} onClick={() => setReload(value => value + 1)}>목록 새로고침</button>
      </div>
      <p>활성 라이브러리 {catalog.scenes.length}편 · 앱과 같은 화면·음성으로 확인합니다. 학습 이력에는 기록되지 않습니다.</p>
      <div className="admin-scene-browser-filters">
        <label>트리거<select value={trigger} onChange={filter(setTrigger)} disabled={loading}>
          <option value="">모든 트리거</option>
          {catalog.triggers.map(value => <option key={value} value={value}>{value}</option>)}
        </select></label>
        {catalog.chapters.length > 0 && <label>챕터<select value={chapter} onChange={filter(setChapter)} disabled={loading}>
          <option value="">모든 챕터</option>
          {catalog.chapters.map(item => <option key={item.id} value={item.id}>{item.title}</option>)}
          {catalog.scenes.some(item => !item.chapterId) && <option value="__none">챕터 없음</option>}
        </select></label>}
        <label>Scene<select value={selectedId || ''} disabled={loading || !filtered.length} onChange={event => setIndex(filtered.findIndex(item => item.id === event.target.value))}>
          {!filtered.length && <option value="">Scene 없음</option>}
          {filtered.map(item => <option key={item.id} value={item.id}>{item.title === item.id ? item.id : `${item.title} · ${item.id}`}</option>)}
        </select></label>
      </div>
      <nav className="admin-scene-browser-navigation" aria-label="Scene 이동">
        <button type="button" disabled={loading || index <= 0 || !filtered.length} onClick={() => setIndex(value => value - 1)}>← 이전 Scene</button>
        <span aria-live="polite">{filtered.length ? index + 1 : 0} / {filtered.length}</span>
        <button type="button" disabled={loading || index >= filtered.length - 1} onClick={() => setIndex(value => value + 1)}>다음 Scene →</button>
        <button type="button" disabled={!selectedId || loading || sceneLoading} onClick={() => setReplay(value => value + 1)}>처음부터 재생</button>
      </nav>
    </header>
    {loading && <p role="status">Scene 목록을 불러오는 중…</p>}
    {error && <p role="alert">{error}</p>}
    {!loading && !error && !filtered.length && <p role="status">조건에 맞는 Scene이 없습니다.</p>}
    {!loading && !error && selectedId && <section className="admin-scene-browser-preview">
      <div className="admin-scene-browser-caption">
        <strong>{selectedId}</strong><span>{filtered[index].frameCount} 프레임 · {filtered[index].trigger}</span>
        {finished && <span role="status">재생 완료 · 이전/다음 Scene으로 이동하거나 다시 재생할 수 있습니다.</span>}
      </div>
      {sceneLoading && <p role="status">Scene을 불러오는 중…</p>}
      {sceneError && <div role="alert">{sceneError} <button onClick={() => setReplay(value => value + 1)}>다시 시도</button></div>}
      {scene && !sceneLoading && !sceneError && (scene.meta?.kind === 'learning-coaching'
        ? <LearningCoach key={`${selectedId}:${replay}`} scenes={[scene]} trigger={scene.trigger} visitKey={`${selectedId}:${replay}`}
          actor={scene.frames?.[0]?.actor} variables={{ currentIndex: 1, cardIndex: 1, totalCount: 100, testIndex: 4, feedback: 'AI feedback / AI 채점 의견' }} />
        : <ScenePlayer key={`${selectedId}:${replay}`} scene={scene} embedded
        fetchAudio={apiClient.getAdminSceneFrameAudio} onContinue={() => setFinished(true)}
        continueLabel="Scene 끝" nextLabel="다음 대사" />)}
      {scene?.previewUsesExamples && <p className="admin-scene-browser-note">이름·날짜·대회 정보는 미리보기용 예시입니다.</p>}
    </section>}
  </div>;
}
